import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import jsPDF from 'jspdf';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFEstatePlanning = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('nomination');
    const [estateData, setEstateData] = useState({});
    const [documentation, setDocumentation] = useState([]);
    const [taxImplications, setTaxImplications] = useState({});
    
    // Death Benefit Nomination (Tests 873-881)
    const [nominationType, setNominationType] = useState('binding');
    const [beneficiaries, setBeneficiaries] = useState([
        { id: 1, name: '', percentage: 0 },
        { id: 2, name: '', percentage: 0 }
    ]);
    
    const totalPercentage = beneficiaries.reduce((sum, b) => sum + (b.percentage || 0), 0);
    const validUntil = nominationType === 'binding' 
        ? new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toISOString().split('T')[0]
        : 'N/A';

    // Estate planning components
    const estateComponents = {
        will: {
            name: 'Will & Testament',
            description: 'Legal document outlining asset distribution',
            status: 'Not Created',
            priority: 'Critical',
            icon: '📜',
            requirements: [
                'Appoint executor and trustee',
                'Specify SMSF beneficiary arrangements',
                'Address potential conflicts between family members',
                'Include backup beneficiaries'
            ]
        },
        binding_nomination: {
            name: 'Binding Death Benefit Nomination',
            description: 'Legal direction for SMSF benefit distribution',
            status: 'Not Created',
            priority: 'Critical',
            icon: '🎯',
            requirements: [
                'Nominate primary and contingent beneficiaries',
                'Specify percentage allocations',
                'Ensure compliance with SMSF regulations',
                'Review and update every 3 years'
            ]
        },
        power_of_attorney: {
            name: 'Power of Attorney',
            description: 'Legal authority for financial decisions',
            status: 'Not Created',
            priority: 'High',
            icon: '⚖️',
            requirements: [
                'Appoint trusted individual for financial decisions',
                'Specify scope of authority',
                'Include SMSF management provisions',
                'Consider medical power of attorney'
            ]
        },
        trust_structure: {
            name: 'Trust Structure Review',
            description: 'Optimize SMSF trust deed provisions',
            status: 'Needs Review',
            priority: 'Medium',
            icon: '🏛️',
            requirements: [
                'Review trust deed for estate planning provisions',
                'Consider testamentary trust options',
                'Evaluate trustee succession arrangements',
                'Assess tax-effective distribution strategies'
            ]
        }
    };

    // Beneficiary analysis
    const beneficiaryAnalysis = {
        primary: [
            { name: 'Spouse/Partner', relationship: 'Spouse', percentage: 60, taxStatus: 'Tax-Free' },
            { name: 'Adult Children', relationship: 'Children', percentage: 30, taxStatus: 'Taxable' },
            { name: 'Charitable Organization', relationship: 'Charity', percentage: 10, taxStatus: 'Tax-Free' }
        ],
        contingent: [
            { name: 'Grandchildren', relationship: 'Grandchildren', percentage: 50, taxStatus: 'Taxable' },
            { name: 'Siblings', relationship: 'Siblings', percentage: 30, taxStatus: 'Taxable' },
            { name: 'Charitable Foundation', relationship: 'Charity', percentage: 20, taxStatus: 'Tax-Free' }
        ]
    };

    // Tax implications analysis
    const analyzeTaxImplications = () => {
        return {
            death_benefits: {
                spouse: {
                    tax_rate: 0,
                    description: 'Tax-free if paid to spouse or dependent child under 18',
                    conditions: ['Lump sum or pension', 'Within 6 months of death']
                },
                adult_children: {
                    tax_rate: 15,
                    description: 'Taxable component taxed at 15% + Medicare levy',
                    conditions: ['Lump sum payment', 'Taxable component only']
                },
                non_dependents: {
                    tax_rate: 30,
                    description: 'Taxable component taxed at 30% + Medicare levy',
                    conditions: ['Lump sum payment', 'Higher tax rate applies']
                }
            },
            estate_tax: {
                total_value: 2400000,
                tax_free_threshold: 18200000,
                potential_tax: 0,
                status: 'Under Threshold'
            },
            strategies: [
                'Consider testamentary trusts for adult children',
                'Maximize spouse benefits to minimize tax',
                'Review pension vs lump sum options',
                'Consider insurance bonds for tax efficiency'
            ]
        };
    };

    // Documentation checklist
    const generateDocumentationChecklist = () => {
        return [
            {
                category: 'Legal Documents',
                items: [
                    { name: 'Current Will and Testament', status: 'Missing', priority: 'Critical' },
                    { name: 'Binding Death Benefit Nomination', status: 'Missing', priority: 'Critical' },
                    { name: 'Power of Attorney (Financial)', status: 'Missing', priority: 'High' },
                    { name: 'Power of Attorney (Medical)', status: 'Missing', priority: 'High' },
                    { name: 'Enduring Guardianship', status: 'Missing', priority: 'Medium' }
                ]
            },
            {
                category: 'SMSF Documents',
                items: [
                    { name: 'Trust Deed (Current Version)', status: 'Available', priority: 'High' },
                    { name: 'Trustee Minutes', status: 'Available', priority: 'Medium' },
                    { name: 'Investment Strategy', status: 'Available', priority: 'Medium' },
                    { name: 'Insurance Policies', status: 'Missing', priority: 'High' },
                    { name: 'Succession Plan', status: 'Missing', priority: 'Critical' }
                ]
            },
            {
                category: 'Financial Records',
                items: [
                    { name: 'Asset Register', status: 'Available', priority: 'High' },
                    { name: 'Bank Account Details', status: 'Available', priority: 'High' },
                    { name: 'Investment Statements', status: 'Available', priority: 'Medium' },
                    { name: 'Insurance Details', status: 'Missing', priority: 'High' },
                    { name: 'Debt Information', status: 'Available', priority: 'High' }
                ]
            }
        ];
    };

    useEffect(() => {
        setTaxImplications(analyzeTaxImplications());
        setDocumentation(generateDocumentationChecklist());
        setEstateData({
            total_value: 2400000,
            complexity_level: 'Medium',
            completion_percentage: 25,
            last_review: '2023-01-15'
        });
    }, []);

    const renderNomination = () => (
        <div className="space-y-6">
            {/* Death Benefit Nomination Form */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Death Benefit Nomination</h3>
                
                {/* Nomination Type */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nomination Type</label>
                    <div className="flex gap-4">
                        <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer flex-1 ${nominationType === 'binding' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}">
                            <input
                                type="radio"
                                value="binding"
                                checked={nominationType === 'binding'}
                                onChange={(e) => setNominationType(e.target.value)}
                                className="mr-2"
                            />
                            <div>
                                <div className="font-medium">Binding Nomination</div>
                                <div className="text-xs text-gray-600">Trustees must follow (valid 3 years)</div>
                            </div>
                        </label>
                        <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer flex-1 ${nominationType === 'non-binding' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                            <input
                                type="radio"
                                value="non-binding"
                                checked={nominationType === 'non-binding'}
                                onChange={(e) => setNominationType(e.target.value)}
                                className="mr-2"
                            />
                            <div>
                                <div className="font-medium">Non-Binding Nomination</div>
                                <div className="text-xs text-gray-600">Trustees have discretion</div>
                            </div>
                        </label>
                    </div>
                </div>
                
                {/* Beneficiaries */}
                <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Beneficiaries</h4>
                    {beneficiaries.map((beneficiary, index) => (
                        <div key={beneficiary.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Beneficiary {index + 1} Name
                                </label>
                                <input
                                    type="text"
                                    value={beneficiary.name}
                                    onChange={(e) => {
                                        const newBeneficiaries = [...beneficiaries];
                                        newBeneficiaries[index].name = e.target.value;
                                        setBeneficiaries(newBeneficiaries);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Enter beneficiary name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Percentage (%)
                                </label>
                                <input
                                    {...getNumericInputProps()}
                                    value={beneficiary.percentage}
                                    onChange={(e) => {
                                        const newBeneficiaries = [...beneficiaries];
                                        newBeneficiaries[index].percentage = Number(e.target.value) || 0;
                                        setBeneficiaries(newBeneficiaries);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                    ))}
                    
                    {/* Total % Check */}
                    <div className={`p-4 rounded-lg border ${totalPercentage === 100 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex justify-between items-center">
                            <span className="font-medium">Total Allocation</span>
                            <span className={`text-2xl font-bold ${totalPercentage === 100 ? 'text-green-700' : 'text-red-700'}`}>
                                {totalPercentage}%
                            </span>
                        </div>
                        {totalPercentage !== 100 && (
                            <p className="text-sm text-red-600 mt-1">Must total 100%</p>
                        )}
                    </div>
                    
                    {/* Valid Until */}
                    {nominationType === 'binding' && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex justify-between items-center">
                                <span className="font-medium">Nomination Valid Until</span>
                                <span className="text-lg font-bold text-blue-700">{validUntil}</span>
                            </div>
                            <p className="text-xs text-blue-600 mt-1">Binding nominations expire after 3 years and must be renewed</p>
                        </div>
                    )}
                </div>
                
                {/* Generate Document */}
                <div className="mt-6 flex justify-end">
                    <button
                        disabled={totalPercentage !== 100}
                        onClick={() => {
                            const doc = new jsPDF();
                            doc.setFontSize(18);
                            doc.setFont(undefined, 'bold');
                            doc.text('Death Benefit Nomination', 105, 20, { align: 'center' });
                            doc.setFontSize(10);
                            doc.text(`Type: ${nominationType === 'binding' ? 'Binding' : 'Non-Binding'}`, 105, 30, { align: 'center' });
                            
                            let y = 45;
                            doc.setFontSize(11);
                            doc.setFont(undefined, 'bold');
                            doc.text('Beneficiaries', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            beneficiaries.forEach((b, i) => {
                                doc.text(`${i + 1}. ${b.name || 'Not specified'}: ${b.percentage}%`, 14, y);
                                y += 5;
                            });
                            y += 10;
                            
                            if (nominationType === 'binding') {
                                doc.setFontSize(11);
                                doc.setFont(undefined, 'bold');
                                doc.text('Validity', 14, y);
                                y += 8;
                                doc.setFontSize(9);
                                doc.setFont(undefined, 'normal');
                                doc.text(`Valid until: ${validUntil}`, 14, y);
                                y += 5;
                                doc.text('Must be witnessed by 2 adults (not beneficiaries)', 14, y);
                                y += 10;
                            }
                            
                            doc.setFontSize(8);
                            doc.setFont(undefined, 'italic');
                            doc.text('This document should be reviewed with a legal professional.', 14, 280);
                            
                            doc.save(`Death_Benefit_Nomination_${new Date().toISOString().split('T')[0]}.pdf`);
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Generate Nomination Document
                    </button>
                </div>
            </div>
            
            {/* Outputs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Requirements & Information</h3>
                
                {/* Test 883: Witness Requirements */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                    <h4 className="font-semibold text-yellow-800 mb-2">Witness Requirements</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• 2 adult witnesses required (18+ years)</li>
                        <li>• Witnesses cannot be beneficiaries or their spouses</li>
                        <li>• Must sign in presence of member</li>
                        <li>• Witnesses must sign in presence of each other</li>
                    </ul>
                </div>
                
                {/* Test 884: Tax Implications */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Death Benefit Tax Implications</h4>
                    <div className="text-sm text-blue-700 space-y-2">
                        <p><strong>Tax-Free Recipients:</strong> Spouse, children under 18, financial dependents</p>
                        <p><strong>Taxable Recipients:</strong> Adult children (not financially dependent)</p>
                        <p className="mt-2"><strong>Tax Rates:</strong></p>
                        <ul className="list-disc ml-5">
                            <li>Tax-free component: 0% tax</li>
                            <li>Taxable component (lump sum): 15% + Medicare Levy (17%)</li>
                            <li>Taxable component (pension): Marginal tax rate (15% offset)</li>
                        </ul>
                    </div>
                </div>
                
                {/* Test 885: Review Due Date */}
                {nominationType === 'binding' && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <h4 className="font-semibold text-orange-800 mb-2">⏰ Review Due Date</h4>
                        <p className="text-sm text-orange-700">
                            Binding nomination expires on: <strong>{validUntil}</strong>
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                            Reminder: Review and renew before expiry to maintain binding status
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Estate Planning Status */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Estate Planning Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(estateComponents).map(([key, component]) => (
                        <div key={key} className={`p-4 border-2 rounded-lg ${
                            component.status === 'Completed' ? 'border-green-500 bg-green-50' :
                            component.status === 'In Progress' ? 'border-yellow-500 bg-yellow-50' :
                            'border-red-500 bg-red-50'
                        }`}>
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-2">{component.icon}</span>
                                <div>
                                    <h4 className="font-semibold text-gray-800">{component.name}</h4>
                                    <p className="text-xs text-gray-600">{component.description}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Status:</span>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                        component.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                        component.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {component.status}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Priority:</span>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                        component.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                                        component.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                        'bg-blue-100 text-blue-800'
                                    }`}>
                                        {component.priority}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SMSF Estate Value */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">SMSF Estate Value Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                            ${estateData.total_value?.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Total SMSF Value</div>
                    </div>
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                        <div className="text-3xl font-bold text-green-600">
                            {estateData.completion_percentage}%
                        </div>
                        <div className="text-sm text-green-700">Planning Completion</div>
                    </div>
                    <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">
                            {estateData.complexity_level}
                        </div>
                        <div className="text-sm text-purple-700">Complexity Level</div>
                    </div>
                </div>
            </div>

            {/* Beneficiary Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Beneficiary Analysis</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Primary Beneficiaries */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Primary Beneficiaries</h4>
                        <div className="space-y-3">
                            {beneficiaryAnalysis.primary.map((beneficiary, index) => (
                                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div>
                                        <div className="font-medium text-gray-800">{beneficiary.name}</div>
                                        <div className="text-sm text-gray-600">{beneficiary.relationship}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-gray-800">{beneficiary.percentage}%</div>
                                        <div className={`text-xs px-2 py-1 rounded ${
                                            beneficiary.taxStatus === 'Tax-Free' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {beneficiary.taxStatus}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Contingent Beneficiaries */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Contingent Beneficiaries</h4>
                        <div className="space-y-3">
                            {beneficiaryAnalysis.contingent.map((beneficiary, index) => (
                                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div>
                                        <div className="font-medium text-gray-800">{beneficiary.name}</div>
                                        <div className="text-sm text-gray-600">{beneficiary.relationship}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-gray-800">{beneficiary.percentage}%</div>
                                        <div className={`text-xs px-2 py-1 rounded ${
                                            beneficiary.taxStatus === 'Tax-Free' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {beneficiary.taxStatus}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTaxAnalysis = () => (
        <div className="space-y-6">
            {/* Tax Implications */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Implications Analysis</h3>
                <div className="space-y-6">
                    {/* Death Benefits Tax */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Death Benefits Taxation</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.entries(taxImplications.death_benefits).map(([key, tax]) => (
                                <div key={key} className="p-4 border border-gray-200 rounded-lg">
                                    <h5 className="font-semibold text-gray-800 mb-2 capitalize">
                                        {key.replace('_', ' ')}
                                    </h5>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Tax Rate:</span>
                                            <span className="font-bold text-gray-800">{tax.tax_rate}%</span>
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">{tax.description}</div>
                                        <div className="text-xs text-gray-500">
                                            <strong>Conditions:</strong> {tax.conditions.join(', ')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Estate Tax Analysis */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Estate Tax Analysis</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">
                                    ${taxImplications.estate_tax?.total_value?.toLocaleString()}
                                </div>
                                <div className="text-sm text-blue-700">Total Estate Value</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                    ${taxImplications.estate_tax?.tax_free_threshold?.toLocaleString()}
                                </div>
                                <div className="text-sm text-green-700">Tax-Free Threshold</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">
                                    {taxImplications.estate_tax?.potential_tax}
                                </div>
                                <div className="text-sm text-purple-700">Potential Tax</div>
                            </div>
                        </div>
                        <div className="mt-4 p-4 bg-green-50 rounded-lg">
                            <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-700">Estate Tax Status:</span>
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                    {taxImplications.estate_tax?.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tax Optimization Strategies */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Tax Optimization Strategies</h4>
                        <div className="space-y-3">
                            {taxImplications.strategies?.map((strategy, index) => (
                                <div key={index} className="flex items-start p-3 border border-gray-200 rounded-lg">
                                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-700">{strategy}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDocumentation = () => (
        <div className="space-y-6">
            {/* Documentation Checklist */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Documentation Checklist</h3>
                <div className="space-y-6">
                    {documentation.map((category, categoryIndex) => (
                        <div key={categoryIndex}>
                            <h4 className="font-semibold text-gray-700 mb-4">{category.category}</h4>
                            <div className="space-y-2">
                                {category.items.map((item, itemIndex) => (
                                    <div key={itemIndex} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                        <div className="flex items-center">
                                            <input 
                                                type="checkbox" 
                                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                checked={item.status === 'Available'}
                                            />
                                            <span className="text-gray-800">{item.name}</span>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <span className={`text-xs px-2 py-1 rounded ${
                                                item.status === 'Available' ? 'bg-green-100 text-green-800' :
                                                item.status === 'Missing' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {item.status}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded ${
                                                item.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                                                item.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                                'bg-blue-100 text-blue-800'
                                            }`}>
                                                {item.priority}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Action Items */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Priority Action Items</h3>
                <div className="space-y-4">
                    {[
                        {
                            action: 'Create Will and Testament',
                            priority: 'Critical',
                            timeline: '30 days',
                            responsible: 'Solicitor',
                            cost: '$800-1,200'
                        },
                        {
                            action: 'Establish Binding Death Benefit Nomination',
                            priority: 'Critical',
                            timeline: '14 days',
                            responsible: 'SMSF Administrator',
                            cost: '$200-400'
                        },
                        {
                            action: 'Set up Power of Attorney',
                            priority: 'High',
                            timeline: '21 days',
                            responsible: 'Solicitor',
                            cost: '$400-600'
                        },
                        {
                            action: 'Review Trust Deed',
                            priority: 'Medium',
                            timeline: '60 days',
                            responsible: 'SMSF Administrator',
                            cost: '$300-500'
                        }
                    ].map((action, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            action.priority === 'Critical' ? 'border-red-500 bg-red-50' :
                            action.priority === 'High' ? 'border-orange-500 bg-orange-50' :
                            'border-blue-500 bg-blue-50'
                        }`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-800">{action.action}</h4>
                                    <p className="text-sm text-gray-600">
                                        <strong>Responsible:</strong> {action.responsible} | 
                                        <strong> Timeline:</strong> {action.timeline} | 
                                        <strong> Cost:</strong> {action.cost}
                                    </p>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded ml-4 ${
                                    action.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                                    action.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                    'bg-blue-100 text-blue-800'
                                }`}>
                                    {action.priority}
                                </span>
                            </div>
                            <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                                Take Action
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Estate Planning</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('nomination')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'nomination' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Death Benefit Nomination
                    </button>
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('tax')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'tax' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Tax Analysis
                    </button>
                    <button
                        onClick={() => setActiveTab('documentation')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'documentation' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Documentation
                    </button>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'nomination' && renderNomination()}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'tax' && renderTaxAnalysis()}
            {activeTab === 'documentation' && renderDocumentation()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Last Review: {estateData.last_review}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Estate Plan
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Schedule Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFEstatePlanning.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFEstatePlanning;
